import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import { celebratePerfect, celebrateGood, celebrateStandard } from "../lib/confetti";
import { T, ALL_LABELS } from "../lib/i18n";
import { useNetworkStatus, useServerTrouble, reportServerResult, serverNow, localBackupClear, startPolling } from "../lib/net";
import { useVersionCheck } from "../lib/version";
import { useTabSleep, STUDENT_IDLE_SLEEP } from "../lib/sleep";
import { dbLoadSession, dbLoadSessionStatus, dbEnsureGroupRef, dbLoadLiveSession, dbJoinGroup, dbRecordScratch, dbFinishGroup, dbLoadGroupResult, getGroupRef } from "../lib/db";
import { Dialog, AppHeader, QuestionImages, QuestionText, ServerTroubleBanner, SleepBanner, SessionClosed } from "../components/common";
import { ScratchCell } from "../components/ScratchCell";
import { questionOrder, optionOrder, inverseOrder, visibleShuffle } from "../lib/shuffle";

// ─────────────────────────────────────────
//  PLAY SCREEN — with persistence + sync
// ─────────────────────────────────────────
const SCRATCH_TIMEOUT = 15000; // ms per försök
const SCRATCH_RETRIES = 2;     // alltså tre försök totalt
const SAVING_SLOW_AFTER = 1500; // visa "Sparar…" först om det dröjer
const FINISH_WAIT = 20000;      // så länge väntar avslut på köade skrap

// Server state is authoritative — scoring happens in record_scratch().
// Men svaren kan komma i fel ordning: en hämtning som lästes innan ett skrap
// sparades kan landa efter skrapets eget svar. Serverns läge per fråga växer
// bara (försök läggs till, en klar fråga öppnas aldrig igen), så ett läge som
// ligger efter vårt eget behålls inte — annars "försvinner" skrapet en stund.
function mergeStates(local, remote) {
  if (!Array.isArray(remote) || remote.length !== local.length) return local;
  let changed = false;
  const merged = local.map((lq, qi) => {
    const rq = remote[qi];
    if (!rq || typeof rq !== "object") return lq;
    const rAtt = Array.isArray(rq.attempts) ? rq.attempts : [];
    const prefix = rAtt.length <= lq.attempts.length && rAtt.every((a, i) => a === lq.attempts[i]);
    if (prefix && (rAtt.length < lq.attempts.length || (lq.done && !rq.done))) return lq;
    const same = rAtt.length === lq.attempts.length
      && rAtt.every((a, i) => a === lq.attempts[i])
      && !!rq.done === !!lq.done
      && (rq.score ?? null) === (lq.score ?? null)
      && (rq.correct ?? lq.correct) === lq.correct;
    if (same) return lq;
    changed = true;
    return { attempts: rAtt, done: !!rq.done, score: rq.score ?? null, correct: rq.correct ?? lq.correct };
  });
  return changed ? merged : local;
}

function PlayScreen({ session, groupName, onDone, onHome, onExit, onHelp, lang, setLang, theme, setTheme, G, css }) {
  const { numQ, numOptions = 4 } = session;
  // Live session data (config/lock/rationale) — polled, kept in state instead
  // of mutating the session prop (mutation never triggered re-renders, so
  // teacher-started timers were invisible to idle students)
  const [live, setLive] = useState(session);
  const liveRef = useRef(live);
  liveRef.current = live;
  const questions = live.questions || [];
  const showRationale = live.showRationale || false;
  const LABELS = ALL_LABELS.slice(0, numOptions);
  const t = T[lang];

  const [states, setStates] = useState(() => Array.from({ length: numQ }, () => ({ attempts: [], done: false, score: null })));
  const [loading, setLoading] = useState(true);
  // Gruppen har redan avslutat (servern: in_progress = false). Då är skrapvyn
  // ett läsläge: inget går att skrapa, inget auto-avslut, ingen ny konfetti.
  const [finished, setFinished] = useState(false);
  const [showQuestion, setShowQuestion] = useState({});
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [expandedDone, setExpandedDone] = useState({});
  const [recentlyDone, setRecentlyDone] = useState({});
  const activeQRef = useRef(null);
  const statesRef = useRef(states);
  const [scratchNonce, setScratchNonce] = useState(0); // bump to re-cover cells after a failed scratch
  const [joinErr, setJoinErr] = useState(null);
  const [timerNow, setTimerNow] = useState(serverNow());
  const [timerWarningDismissed, setTimerWarningDismissed] = useState(null); // "5" or "2"
  const timerFinishedRef = useRef(false);
  const [savedToast, setSavedToast] = useState(null); // { text }
  const savedToastTimer = useRef(null);

  // Gruppens visningsordning (slumpad ordning är påslagen på sessionen).
  // Allt annat i appen räknar i kanonisk ordning; översättningen sker bara här.
  //   qOrder[visningsposition] = kanoniskt frågeindex
  //   optOrder(qi)[pos]        = kanoniskt alternativindex
  const [shuffle, setShuffle] = useState(null);
  // Bara det gruppen kan se i appen slumpas (se visibleShuffle): en tRAT med
  // frågorna på papper får annars bokstäver som inte stämmer med bladet.
  const shown = visibleShuffle(shuffle, questions, numQ, LABELS);
  const qOrder = questionOrder(shown, numQ);
  const optOrder = qi => optionOrder(shown, qi, numOptions);

  // Keep ref in sync with state
  useEffect(() => { statesRef.current = states; }, [states]);

  const totalScore = states.reduce((s, q) => s + (q.score ?? 0), 0);
  const doneCount = states.filter(q => q.done).length;
  const allDone = doneCount === numQ;
  // Nästa obesvarade fråga i den ordning gruppen ser dem — inte den kanoniska
  const firstActiveQ = (() => {
    const pos = qOrder.findIndex(qi => !states[qi]?.done);
    return pos === -1 ? -1 : qOrder[pos];
  })();

  // Scroll to the next question first AFTER the finished question has
  // collapsed (advanceNonce bumps then) — scrolling the instant the answer
  // landed yanked the result out of view before students could read it
  const [advanceNonce, setAdvanceNonce] = useState(0);
  useEffect(() => {
    if (advanceNonce > 0 && !showAllQuestions && activeQRef.current && doneCount > 0 && !allDone) {
      activeQRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [advanceNonce]);
  const maxScore = numQ * 4;

  // Timer logic — uses earliest of: per-group timer OR global timer from teacher dashboard
  const [groupTimerEnd, setGroupTimerEnd] = useState(null);
  const globalTimerEnd = live.config?.timer_end || null;
  // Pick the earliest deadline that exists
  const timerEnd = (groupTimerEnd && globalTimerEnd)
    ? (new Date(groupTimerEnd) < new Date(globalTimerEnd) ? groupTimerEnd : globalTimerEnd)
    : groupTimerEnd || globalTimerEnd;
  const timerSecsLeft = timerEnd ? Math.max(0, Math.floor((new Date(timerEnd).getTime() - timerNow) / 1000)) : null;
  const timerMins = timerSecsLeft != null ? Math.floor(timerSecsLeft / 60) : null;
  const timerSecs = timerSecsLeft != null ? timerSecsLeft % 60 : null;
  const timerExpired = timerSecsLeft === 0 && timerEnd != null;
  const timerWarning5 = timerSecsLeft != null && timerSecsLeft <= 300 && timerSecsLeft > 120;
  const timerWarning2 = timerSecsLeft != null && timerSecsLeft <= 120 && timerSecsLeft > 0;
  // Läses av hämtloopen nedan (tätare frågor nära tidsslut) utan att starta om den
  const timerSecsLeftRef = useRef(null);
  timerSecsLeftRef.current = timerSecsLeft;
  const realtimeOkRef = useRef(false); // sätts när realtidskanalen är uppkopplad

  // Tick the clock every second when timer is active
  useEffect(() => {
    if (!timerEnd) return;
    const i = setInterval(() => setTimerNow(serverNow()), 1000);
    return () => clearInterval(i);
  }, [timerEnd]);

  // Auto-finish when timer expires — but never before the server state has
  // loaded. Öppnar en student sidan igen efter att tiden gått ut är
  // timerExpired sant redan vid första renderingen, medan `states` fortfarande
  // är det tomma startläget. Då avslutades gruppen med tomt läge och
  // resultatsidan visade "0 av 52 / Ej påbörjad" trots att allt låg på servern.
  useEffect(() => {
    // En grupp som redan avslutat ska inte avslutas igen: då skickades den
    // direkt tillbaka till resultatsidan när tiden gått ut, och "Tillbaka
    // till frågorna" såg ut att inte göra någonting.
    if (timerExpired && !loading && !finished && !timerFinishedRef.current) {
      timerFinishedRef.current = true;
      // Sista koll innan gruppen avslutas: har läraren förlängt tiden (+5) sedan
      // vi senast frågade? Skrapvyn frågar inte lika tätt som förut, så en
      // förlängning i sista stund kan annars missas.
      // Bara den lätta statusen, inte hela tRAT:en: när en gemensam timer går
      // ut frågar alla enheter i samma sekund.
      (async () => {
        const [st, saved] = await Promise.all([
          dbLoadSessionStatus(session.sessionId).catch(() => null),
          dbLoadGroupResult(session.sessionId, groupName).catch(() => null),
        ]);
        const later = t => t && new Date(t).getTime() > serverNow() + 1000;
        if (later(st?.timerEnd) || later(saved?.timerEnd)) {
          if (later(st?.timerEnd)) setLive(prev => ({ ...prev, config: { ...(prev.config || {}), timer_end: st.timerEnd } }));
          if (saved?.timerEnd) setGroupTimerEnd(saved.timerEnd);
          timerFinishedRef.current = false;
          return;
        }
        finishAndDone();
      })();
    }
  }, [timerExpired, loading, finished]);

  // Re-fetch session periodically to pick up timer, lock status and rationale
  // toggles from the teacher dashboard
  const [isLocked, setIsLocked] = useState(session.sessionLocked || false);
  // Två hämtloopar i stället för en gemensam var femte sekund:
  //  - tRAT:ens status (lås, återkoppling, lärarens timer) var 10:e sekund,
  //    var 5:e när mindre än två minuter återstår så att +5 hinner fram. Bara
  //    statusen hämtas, inte frågorna (se dbLoadLiveSession),
  //  - gruppens läge var 20:e sekund när realtid fungerar (då kommer
  //    lagkamraternas skrap den vägen), annars var 8:e.
  // Båda glesar ut vid fel och pausar när fliken är dold (se startPolling).
  // En flik som somnat (se useTabSleep) hämtar inget; när den vaknar hämtas
  // läget direkt.
  const { asleep, hasSlept } = useTabSleep({ idleAfter: STUDENT_IDLE_SLEEP });
  // Gruppens ref (se db.js) styr realtidskanalen: med den får enheten bara
  // sin egen grupps händelser, inte alla gruppers i hela tRAT:en.
  const [groupRef, setGroupRefState] = useState(() => getGroupRef(session.sessionId, groupName));
  useEffect(() => {
    if (asleep) return undefined;
    const stopSession = startPolling(async () => {
      const update = await dbLoadLiveSession(session.sessionId, { hadRationale: liveRef.current.showRationale });
      if (!update) return false;
      const next = update(liveRef.current);
      setLive(next);
      setIsLocked(next.sessionLocked);
      return true;
    }, { interval: () => (timerSecsLeftRef.current != null && timerSecsLeftRef.current <= 120 ? 5000 : 10000), immediate: hasSlept });
    const stopGroup = startPolling(async () => {
      // Saknas refen (hämtningen misslyckades vid anslutningen) försöker vi igen
      if (!getGroupRef(session.sessionId, groupName)) {
        dbEnsureGroupRef(session.sessionId, groupName).then(r => { if (r) setGroupRefState(r); }).catch(() => {});
      }
      const saved = await dbLoadGroupResult(session.sessionId, groupName);
      if (!saved) return false;
      if (saved.scratchState) setStates(prev => mergeStates(prev, saved.scratchState));
      if (saved.timerEnd) setGroupTimerEnd(saved.timerEnd);
      return true;
    }, { interval: () => (realtimeOkRef.current ? 20000 : 8000), immediate: hasSlept });
    return () => { stopSession(); stopGroup(); };
  }, [session.sessionId, groupName, asleep, hasSlept]);

  // Konfetti när gruppen blir klar under det här besöket — inte varje gång
  // en redan klar grupp går tillbaka till frågorna från resultatsidan.
  const doneAtLoadRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (doneAtLoadRef.current === null) doneAtLoadRef.current = allDone;
    if (!allDone || doneAtLoadRef.current || totalScore === 0) return;
    const id = setTimeout(() => {
      const percentage = (totalScore / maxScore) * 100;
      if (percentage === 100) {
        celebratePerfect();
      } else if (percentage >= 75) {
        celebrateGood();
      } else {
        celebrateStandard();
      }
    }, 400);
    return () => clearTimeout(id);
  }, [allDone, loading]);

  // Clear the toast timer if the component unmounts mid-toast
  useEffect(() => () => clearTimeout(savedToastTimer.current), []);


  // Join (idempotent) on mount: creates the group row server-side if needed,
  // assigns the per-group timer, and returns saved state. Lagkamraternas skrap
  // kommer via realtidskanalen nedan, som använder applyRemoteRef.
  const applyRemoteRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const applySaved = saved => {
      if (cancelled || !saved) return;
      if (saved.scratchState) setStates(prev => mergeStates(prev, saved.scratchState));
      if (saved.timerEnd) setGroupTimerEnd(saved.timerEnd);
      // Ordningen kommer från gruppens rad, inte från enheten: laddar man om
      // eller kommer in på en andra telefon får man samma ordning som de andra.
      // En grupp som redan är klar möter standardordningen — då är tRAT:en
      // över och alla ska prata om samma "fråga 7". Läget låses vid inladdning,
      // så listan inte kastas om mitt framför näsan på gruppen i samma sekund
      // som sista frågan blir klar.
      setShuffle(saved.inProgress === false ? null : (saved.shuffle || null));
      if (saved.inProgress === false) setFinished(true);
    };
    localBackupClear(session.sessionId, groupName); // legacy backups are obsolete
    dbJoinGroup(session.sessionId, groupName, null)
      .then(async res => {
        if (cancelled) return;
        if (res.error) {
          if (res.error === "locked") setIsLocked(true);
          if (res.error === "bad_group_code" || res.error === "not_found") setJoinErr(res.error);
          // Locked/finished groups can still view their state (read-only)
          const saved = await dbLoadGroupResult(session.sessionId, groupName).catch(() => null);
          applySaved(saved);
        } else {
          applySaved(res.result);
        }
        setGroupRefState(getGroupRef(session.sessionId, groupName));
        setLoading(false);
      })
      .catch(async () => {
        const saved = await dbLoadGroupResult(session.sessionId, groupName).catch(() => null);
        applySaved(saved);
        if (!cancelled) setLoading(false);
      });

    // Realtime on the SANITIZED progress table: it carries no attempts, so it
    // can't leak the answer key to other groups (group_results is no longer
    // readable by students). An event on our own row means a teammate scratched
    // on another device — fetch the full state through the token-gated RPC.
    const applyRemote = (saved) => {
      if (cancelled || !saved) return;
      if (saved.timerEnd) setGroupTimerEnd(saved.timerEnd);
      const remoteScratch = saved.scratchState;
      if (!remoteScratch) return;
      const prev = statesRef.current;
      const merged = mergeStates(prev, remoteScratch);
      // Show the result banner on teammates' devices too when a question was
      // just finished from another device
      const newlyDone = merged
        .map((q, i) => (q.done && !prev[i]?.done ? i : null))
        .filter(i => i !== null);
      if (newlyDone.length > 0) {
        setRecentlyDone(p => { const n = { ...p }; newlyDone.forEach(i => { n[i] = true; }); return n; });
      }
      setStates(p => mergeStates(p, remoteScratch));
    };

    applyRemoteRef.current = applyRemote;
    return () => { cancelled = true; };
  }, [session.sessionId, groupName]);

  // Realtidskanalen hålls i en egen effekt så att den kan stängas när fliken
  // sover och öppnas igen när den vaknar, utan att gruppen ansluts på nytt.
  // Den filtreras på gruppens ref: varje skrap når då bara gruppens egna
  // enheter, inte alla i salen. Utan ref ingen kanal — gruppens läge hämtas
  // då var 8:e sekund i stället (realtimeOkRef förblir false).
  useEffect(() => {
    if (asleep || !groupRef) return undefined;
    const channel = supabase.channel(`play-${session.sessionId}-${groupRef}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'group_progress',
        filter: `group_ref=eq.${groupRef}`,
      }, (payload) => {
        // En händelse på vår rad: en lagkamrat skrapade på en annan enhet —
        // hämta hela läget via det token-skyddade anropet.
        if (!payload.new) return;
        dbLoadGroupResult(session.sessionId, groupName).then(s => applyRemoteRef.current?.(s)).catch(() => {});
      })
      .subscribe(status => { realtimeOkRef.current = status === "SUBSCRIBED"; });

    return () => { realtimeOkRef.current = false; supabase.removeChannel(channel); };
  }, [session.sessionId, groupName, groupRef, asleep]);

  const [saveErr, setSaveErr] = useState(false);

  // Skrap som står i kö eller väntar på servern får komma fram först (högst
  // 20 s), annars stänger finish_group gruppen och de räknas inte.
  async function finishAndDone() {
    await Promise.race([scratchQueueRef.current, new Promise(r => setTimeout(r, FINISH_WAIT))]);
    try { await dbFinishGroup(session.sessionId, groupName); } catch (e) { /* results page still reachable */ }
    onDone(statesRef.current);
  }

  // Skrapen från en enhet skickas ett i taget, i den ordning de gjordes.
  // Förut skickades de parallellt: när servern var långsam (23 oktober) kunde
  // ett senare skrap nå fram före ett tidigare och räknas som första försöket.
  // Ett skrap som inte får svar skickas igen (servern räknar samma alternativ
  // bara en gång); ger vi till slut upp visas serverns faktiska läge i stället
  // för att rutan bara täcks över.
  const scratchQueueRef = useRef(Promise.resolve());
  const queuedRef = useRef(0);
  const [savingSlow, setSavingSlow] = useState(false);
  const savingSlowTimer = useRef(null);

  function scratch(qi, oi) {
    if (isLocked || finished) return;
    const cur = statesRef.current[qi];
    if (!cur || cur.done || cur.attempts.includes(oi)) return;
    queuedRef.current += 1;
    if (queuedRef.current === 1) {
      clearTimeout(savingSlowTimer.current);
      savingSlowTimer.current = setTimeout(() => setSavingSlow(true), SAVING_SLOW_AFTER);
    }
    scratchQueueRef.current = scratchQueueRef.current
      .then(() => sendScratch(qi, oi))
      .catch(() => {})
      .finally(() => {
        queuedRef.current -= 1;
        if (queuedRef.current === 0) { clearTimeout(savingSlowTimer.current); setSavingSlow(false); }
      });
  }

  async function sendScratch(qi, oi) {
    // Ett skrap som stod i kö kan ha blivit överflödigt (frågan klar)
    const cur = statesRef.current[qi];
    if (!cur || cur.done || cur.attempts.includes(oi)) return;
    const sv = lang === "sv";
    const toast = (text, isWrong) => {
      clearTimeout(savedToastTimer.current);
      setSavedToast({ text, isWrong });
      savedToastTimer.current = setTimeout(() => setSavedToast(null), 2500);
    };
    try {
      let res;
      for (let attempt = 0; ; attempt++) {
        try {
          res = await dbRecordScratch(session.sessionId, groupName, qi, oi, { timeoutMs: SCRATCH_TIMEOUT });
          break;
        } catch (err) {
          reportServerResult(false);
          if (attempt >= SCRATCH_RETRIES) throw err;
          await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      }
      if (!res || res.error) {
        setScratchNonce(n => n + 1); // re-cover the cell
        if (res?.error === "locked") setIsLocked(true);
        const msg =
          res?.error === "locked" ? (sv ? "tRAT:en är låst av läraren." : "The tRAT has been locked by the teacher.")
          : res?.error === "finished" ? (sv ? "Er grupp har avslutat — inga fler svar registreras." : "Your group has finished — no more answers are recorded.")
          : res?.error === "time_up" ? (sv ? "Tiden är ute." : "Time is up.")
          : res?.error === "not_joined" ? (sv ? "Gå med i gruppen igen via sessionskoden." : "Rejoin your group via the session code.")
          : (sv ? "Kunde inte registrera svaret — försök igen." : "Could not record the answer — try again.");
        toast(msg, true);
        return;
      }
      setSaveErr(false);
      reportServerResult(true);
      const newStates = res.state;
      setStates(prev => mergeStates(prev, newStates));
      const qNew = (newStates && newStates[qi]) || {};
      if (qNew.done) {
        // The question stays expanded with a result banner until the group
        // clicks "Nästa fråga →" — the pace is theirs, nothing auto-advances.
        // (The banner announces the result, so no toast here.)
        setRecentlyDone(p => ({ ...p, [qi]: true }));
      } else {
        toast(sv ? "Fel svar – försök igen" : "Wrong answer – try again", true);
      }
    } catch (e) {
      setSaveErr(true);
      // Servern kan ha sparat skrapet trots att svaret aldrig kom: visa det
      // som faktiskt är sparat innan rutan eventuellt täcks över igen.
      const saved = await dbLoadGroupResult(session.sessionId, groupName).catch(() => null);
      if (saved?.scratchState) setStates(prev => mergeStates(prev, saved.scratchState));
      setScratchNonce(n => n + 1);
      const kept = saved?.scratchState?.[qi]?.attempts?.includes?.(oi);
      toast(kept
        ? (sv ? "Svaret är sparat." : "The answer is saved.")
        : (sv ? "Ingen kontakt med servern — skrapa igen." : "No contact with the server — scratch again."), !kept);
    }
  }

  const isOnline = useNetworkStatus();
  const serverTrouble = useServerTrouble();
  // Ingen omladdning medan skrap står i kö (de finns bara i minnet)
  useVersionCheck(() => queuedRef.current === 0, asleep);

  if (loading) return <div style={{ ...css.page, display: "flex", alignItems: "center", justifyContent: "center", color: G.muted }}>{t.resuming}</div>;
  if (isLocked) {
    const anyDone = states.some(q => q.done);
    const total = states.reduce((s, q) => s + (q.score ?? 0), 0);
    return <SessionClosed onExit={onExit || onHome} score={anyDone ? { total, max: session.numQ * 4 } : null} lang={lang} G={G} css={css} />;
  }

  return (
    <div style={css.page}>
      <ServerTroubleBanner trouble={serverTrouble && isOnline} audience="student" lang={lang} G={G} />
      <SleepBanner asleep={asleep && !(serverTrouble && isOnline)} lang={lang} G={G} />
      {/* Offline / save error banner */}
      {(!isOnline || saveErr) && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, background: G.red, color: "#fff", textAlign: "center", padding: "8px 16px", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <span>{!isOnline ? (lang === "sv" ? "⚠ Ingen internetanslutning — vänta med att skrapa tills anslutningen är tillbaka" : "⚠ No internet connection — wait to scratch until you are back online") : (lang === "sv" ? "⚠ Kunde inte nå servern — försök igen" : "⚠ Could not reach the server — try again")}</span>
        </div>
      )}
      <div style={(!isOnline || saveErr) ? { marginTop: 36 } : undefined}>
      <AppHeader lang={lang} setLang={setLang} onHome={onHome} subtitle={session.sessionTitle ? `${session.sessionTitle} · ${groupName}` : groupName} theme={theme} setTheme={setTheme} onHelp={onHelp} G={G} css={css}>
        {timerEnd && !timerExpired && (
          <div style={{ background: timerWarning2 ? G.red + "22" : timerWarning5 ? G.gold + "22" : G.surface2, border: `1px solid ${timerWarning2 ? G.red + "66" : timerWarning5 ? G.gold + "66" : G.border}`, borderRadius: 999, padding: "5px 14px", fontFamily: "'DM Mono',monospace", fontSize: 15, fontWeight: 700, color: timerWarning2 ? G.red : timerWarning5 ? G.gold : G.text, display: "flex", alignItems: "center", gap: 6, animation: timerWarning2 ? "timerPulse 1s ease-in-out infinite" : "none" }}>
            <span style={{ fontSize: 14 }}>{timerWarning2 ? "⚠️" : "⏱"}</span>
            {timerMins}:{String(timerSecs).padStart(2, "0")}
          </div>
        )}
        <div style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 999, padding: "5px 16px", fontFamily: "'DM Mono',monospace", fontSize: 14, color: G.muted }}>{t.score} <span style={{ color: G.gold, fontSize: 17, marginLeft: 4 }}>{totalScore}</span></div>
        <div style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 999, padding: "5px 14px", fontFamily: "'DM Mono',monospace", fontSize: 13, color: G.muted }}>{doneCount}/{numQ}</div>
        {(allDone || finished) && <button style={{ ...css.btnPrimary, padding: "7px 16px", fontSize: 14 }} onClick={() => finished ? onDone(states) : finishAndDone()}>{t.seeResults}</button>}
      </AppHeader>
      </div>
      {/* Timer warning banners */}
      {timerWarning5 && timerWarningDismissed !== "5" && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "12px 20px 0" }}>
          <div style={{ background: G.gold + "18", border: `1.5px solid ${G.gold}44`, borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>⏱</span>
              <span style={{ fontSize: 14, color: G.text, fontWeight: 600 }}>{lang === 'sv' ? '5 minuter kvar!' : '5 minutes remaining!'}</span>
            </div>
            <button onClick={() => setTimerWarningDismissed("5")} style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }}>&times;</button>
          </div>
        </div>
      )}
      {timerWarning2 && timerWarningDismissed !== "2" && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "12px 20px 0" }}>
          <div style={{ background: G.red + "18", border: `1.5px solid ${G.red}44`, borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, animation: "timerPulse 1.5s ease-in-out infinite" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <span style={{ fontSize: 14, color: G.text, fontWeight: 600 }}>{lang === 'sv' ? '2 minuter kvar — skynda er!' : '2 minutes left — hurry up!'}</span>
            </div>
            <button onClick={() => setTimerWarningDismissed("2")} style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }}>&times;</button>
          </div>
        </div>
      )}
      {joinErr && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "20px 20px 0" }}>
          <div style={{ background: G.red + "18", border: `2px solid ${G.red}44`, borderRadius: 12, padding: "14px 18px", textAlign: "center" }}>
            <div style={{ fontSize: 14, color: G.text }}>
              {lang === "sv"
                ? "Er grupp kunde inte verifieras — gå med igen via sessionskoden."
                : "Your group could not be verified — rejoin via the session code."}
            </div>
            <button style={{ ...css.btnSecondary, width: "auto", marginTop: 10 }} onClick={onHome}>{t.back}</button>
          </div>
        </div>
      )}
      {finished && (
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "20px 20px 0" }}>
          <div role="status" style={{ background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 18px", fontSize: 14, color: G.text, textAlign: "center" }}>
            {lang === "sv"
              ? "Er grupp har avslutat. Här ser ni hur ni svarade — inget mer går att skrapa."
              : "Your group has finished. This shows how you answered — nothing more can be scratched."}
          </div>
        </div>
      )}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 20px" }}>
        {doneCount > 0 && !allDone && (
          <button onClick={() => { setShowAllQuestions(p => !p); setExpandedDone({}); }} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "6px 14px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", display: "block", margin: "0 auto 16px" }}>
            {showAllQuestions ? (lang === "sv" ? "▲ Dölj besvarade" : "▲ Hide answered") : (lang === "sv" ? "▼ Visa alla frågor" : "▼ Show all questions")}
          </button>
        )}
        {/* Frågorna renderas i gruppens visningsordning; qi är alltid det
            kanoniska indexet, som är det servern och resultatvyn räknar i. */}
        {qOrder.map((qi, displayPos) => {
          const state = states[qi] || { attempts: [], done: false, score: null };
          const qData = questions[qi] || {};
          // Alternativens ordning för just den här frågan. Bokstaven gruppen ser
          // är visningspositionen; texten och skrapet hör till det kanoniska.
          const oOrder = optOrder(qi);
          const oInverse = inverseOrder(oOrder);
          const hasText = qData.text?.trim();
          const hasOptions = LABELS.some(lbl => qData.options?.[lbl]?.trim());
          const hasImages = Array.isArray(qData.images) && qData.images.length > 0;
          const isActive = !state.done;
          const showQ = showQuestion[qi];
          const isCollapsed = state.done && !showAllQuestions && !expandedDone[qi] && !recentlyDone[qi] && qi !== firstActiveQ;
          const isFirstActive = qi === firstActiveQ;
          return (
            <div key={qi} ref={isFirstActive ? activeQRef : undefined} style={{ background: G.surface, border: `1px solid ${state.done && state.score > 0 ? G.correct + "55" : G.border}`, borderRadius: 12, padding: isCollapsed ? "12px 20px" : "18px 20px", marginBottom: 12, transition: "all 0.3s", cursor: state.done ? "pointer" : "default" }}
              onClick={state.done && !isActive ? () => setExpandedDone(p => ({ ...p, [qi]: !isCollapsed ? false : true })) : undefined}
              role={state.done && !isActive ? "button" : undefined}
              tabIndex={state.done && !isActive ? 0 : undefined}
              aria-expanded={state.done && !isActive ? !isCollapsed : undefined}
              onKeyDown={state.done && !isActive ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpandedDone(p => ({ ...p, [qi]: !isCollapsed ? false : true })); } } : undefined}
              onMouseEnter={isCollapsed ? (e) => { e.currentTarget.style.background = G.surface2; } : undefined}
              onMouseLeave={isCollapsed ? (e) => { e.currentTarget.style.background = G.surface; } : undefined}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: isCollapsed ? 0 : 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ ...css.heading, fontSize: isCollapsed ? 16 : 22, color: state.done ? G.muted : G.gold, minWidth: isCollapsed ? 36 : 48, lineHeight: 1 }}>Q{displayPos + 1}</div>
                  {!isCollapsed && <div style={{ width: 1, height: 20, background: G.border }} />}
                  {!isCollapsed && (() => {
                    // Attempt dots use a ✓/✗ glyph in addition to colour so the
                    // result isn't conveyed by colour alone (WCAG 1.4.1).
                    const doneTries = state.attempts.length;
                    const label = state.done
                      ? (lang === "sv" ? `${doneTries} försök, ${state.score > 0 ? "rätt" : "fel"}` : `${doneTries} attempts, ${state.score > 0 ? "correct" : "wrong"}`)
                      : (lang === "sv" ? `${doneTries} försök hittills` : `${doneTries} attempts so far`);
                    return <div style={{ display: "flex", gap: 5 }} role="img" aria-label={label}>
                      {Array(numOptions).fill(0).map((_, d) => { const tried = state.attempts[d]; const isCorrect = tried !== undefined && tried === state.correct; return <span key={d} aria-hidden="true" style={{ width: 11, height: 11, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", fontSize: 8, lineHeight: 1, color: tried === undefined ? "transparent" : "#fff", background: tried === undefined ? G.border : isCorrect ? G.correct : G.red, transition: "background 0.3s" }}>{tried === undefined ? "" : isCorrect ? "✓" : "✗"}</span>; })}
                    </div>;
                  })()}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {/* stopPropagation: kortets klickyta togglar ihopfällning —
                      utan stoppet stängde "Visa fråga" hela kortet igen */}
                  {!isActive && !isCollapsed && (hasText || hasOptions || hasImages) && (
                    <button onClick={e => { e.stopPropagation(); setShowQuestion(p => ({ ...p, [qi]: !p[qi] })); }}
                      style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer", fontFamily: "'DM Mono',monospace" }}>
                      {showQ ? t.hideQuestion : t.showQuestion}
                    </button>
                  )}
                  {state.score !== null && (
                    <div style={{ background: ({ 4: G.correct, 2: G.gold, 1: G.goldDim, 0: G.red }[state.score] || G.muted) + "22", color: ({ 4: G.correct, 2: G.gold, 1: G.goldDim, 0: G.red }[state.score] || G.muted), border: `1px solid ${({ 4: G.correct, 2: G.gold, 1: G.goldDim, 0: G.red }[state.score] || G.muted)}44`, borderRadius: 999, padding: "3px 14px", fontSize: isCollapsed ? 12 : 14, fontFamily: "'DM Mono',monospace", fontWeight: 500 }}>
                      +{state.score} pts
                    </div>
                  )}
                  {state.done && <span style={{ color: G.muted, fontSize: 14, transition: "transform 0.2s", display: "inline-block", transform: isCollapsed ? "rotate(0deg)" : "rotate(90deg)" }}>›</span>}
                </div>
              </div>

              {!isCollapsed && <>
              {/* Question text + options */}
              {(isActive || showQ) && (hasText || hasOptions || hasImages) && (
                <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
                  {hasText && <QuestionText text={qData.text} style={{ fontSize: 14, color: G.text, lineHeight: 1.7, marginBottom: hasOptions ? 10 : 0 }} />}
                  {/* Figurer — vissa frågor har hela sitt innehåll i bilden */}
                  <QuestionImages images={qData.images} G={G} lang={lang} />
                  {hasOptions && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {oOrder.map((canonicalOi, pos) => {
                        const text = qData.options?.[LABELS[canonicalOi]];
                        return text?.trim() ? (
                          <div key={pos} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: G.gold, fontWeight: 600, minWidth: 20 }}>{LABELS[pos]}</span>
                            <span style={{ fontSize: 14, color: G.text, lineHeight: 1.6 }}>{text}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Rationale */}
              {state.done && showRationale && qData.rationale?.trim() && (
                <div style={{ background: G.gold + "12", border: `1px solid ${G.gold}33`, borderRadius: 12, padding: "12px 14px", marginBottom: 14, fontSize: 14, color: G.text, lineHeight: 1.7 }}>
                  <div style={{ ...css.heading, fontSize: 12, color: G.gold, marginBottom: 6 }}>{t.rationale}</div>
                  <QuestionText text={qData.rationale} />
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(88px, 1fr))`, gap: 8 }}>
                {oOrder.map((canonicalOi, pos) => (
                  <ScratchCell key={`${qi}-${pos}-${state.attempts.length}-${scratchNonce}`} label={LABELS[pos]} revealed={state.attempts.includes(canonicalOi)} correct={state.correct === canonicalOi} canScratch={!finished && !state.done && !state.attempts.includes(canonicalOi)} onScratched={() => scratch(qi, canonicalOi)} G={G} lang={lang} />
                ))}
              </div>

              {/* Result banner — shown until the group clicks onwards */}
              {state.done && recentlyDone[qi] && (() => {
                const sv = lang === "sv";
                const ok = state.score > 0;
                const attemptPhrase = state.score === 4 ? (sv ? " på första försöket" : " on the first try")
                  : state.score === 2 ? (sv ? " på andra försöket" : " on the second try")
                  : state.score === 1 ? (sv ? " på tredje försöket" : " on the third try")
                  : "";
                // Serverns svar är kanoniskt — visa den bokstav gruppen såg
                const correctLabel = LABELS[oInverse[state.correct]] ?? "?";
                return (
                  <div role="status" style={{
                    marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 12, flexWrap: "wrap",
                    background: ok ? G.cardCorrectBg : G.cardWrongBg,
                    border: `1.5px solid ${ok ? G.cardCorrectBorder : G.cardWrongBorder}`,
                    borderRadius: 12, padding: "13px 16px",
                    animation: "fadeIn 0.25s ease-out",
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: ok ? G.correct : G.red, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      {ok
                        ? `✓ ${sv ? "Korrekt" : "Correct"}${attemptPhrase} — +${state.score} ${sv ? "poäng" : "pts"}`
                        : `✗ ${sv ? `Rätt svar var ${correctLabel}` : `Correct answer was ${correctLabel}`} — 0 ${sv ? "poäng" : "pts"}`}
                    </div>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setRecentlyDone(p => { const n = { ...p }; delete n[qi]; return n; });
                        if (allDone) finishAndDone();
                        else setAdvanceNonce(n => n + 1);
                      }}
                      style={{ ...css.btnPrimary, padding: "8px 18px", fontSize: 14 }}>
                      {allDone ? (sv ? "Se resultat →" : "See results →") : (sv ? "Nästa fråga →" : "Next question →")}
                    </button>
                  </div>
                );
              })()}
              </>}
            </div>
          );
        })}
        {/* Finish button at bottom of scratch cards */}
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <button onClick={() => { if (finished) { onDone(states); } else if (allDone) { finishAndDone(); } else { setShowFinishConfirm(true); } }} style={{ ...css.btnPrimary, maxWidth: 340, padding: "14px 28px", fontSize: 15 }}>
            {(allDone || finished) ? (lang === 'sv' ? 'Se resultat →' : 'See results →') : (lang === 'sv' ? 'Avsluta tRAT →' : 'Finish tRAT →')}
          </button>
          {!allDone && !finished && <div style={{ fontSize: 13, color: G.muted, marginTop: 8 }}>{doneCount}/{numQ} {lang === 'sv' ? 'frågor besvarade' : 'questions answered'}</div>}
        </div>
      </div>
      {/* Confirmation dialog for finishing with unanswered questions */}
      {showFinishConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <Dialog onClose={() => setShowFinishConfirm(false)} label={lang === 'sv' ? 'Obesvarade frågor' : 'Unanswered questions'} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: "32px 28px", maxWidth: 420, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ ...css.heading, fontSize: 20, marginBottom: 8 }}>{lang === 'sv' ? 'Obesvarade frågor' : 'Unanswered questions'}</h3>
            <p style={{ color: G.sub, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              {lang === 'sv'
                ? `Ni har inte svarat på ${numQ - doneCount} av ${numQ} frågor. Obesvarade frågor ger 0 poäng. Vill ni avsluta ändå?`
                : `You haven't answered ${numQ - doneCount} of ${numQ} questions. Unanswered questions score 0 points. Finish anyway?`}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowFinishConfirm(false)} style={{ ...css.btnSecondary, flex: 1, marginTop: 0 }}>
                {lang === 'sv' ? '← Fortsätt svara' : '← Keep answering'}
              </button>
              <button onClick={() => { setShowFinishConfirm(false); finishAndDone(); }} style={{ ...css.btnPrimary, flex: 1, background: G.gold }}>
                {lang === 'sv' ? 'Avsluta ändå' : 'Finish anyway'}
              </button>
            </div>
          </Dialog>
        </div>
      )}
      {/* Ett skrap väntar ovanligt länge på servern: säg det, så att gruppen
          inte tror att inget hände. Skrap de gör under tiden köas i ordning. */}
      {savingSlow && !savedToast && (
        <div role="status" style={{
          position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)",
          background: G.surface, border: `1px solid ${G.border}`,
          borderRadius: 12, padding: "8px 20px", fontSize: 13, fontWeight: 500,
          color: G.text, fontFamily: "'DM Sans',sans-serif",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 200,
          maxWidth: "calc(100vw - 32px)", textAlign: "center",
        }}>
          {lang === "sv" ? "Sparar… servern svarar långsamt, era svar räknas i den ordning ni skrapade." : "Saving… the server is slow; your answers count in the order you scratched."}
        </div>
      )}
      {/* Saved toast — role=status so screen readers announce the verdict */}
      {savedToast && (
        <div role="status" style={{
          position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)",
          background: G.surface, border: `1px solid ${savedToast.isWrong ? G.red + "55" : G.border}`,
          borderRadius: 12, padding: "8px 20px", fontSize: 13, fontWeight: 500,
          color: savedToast.isWrong ? G.red : G.text, fontFamily: "'DM Sans',sans-serif",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 200,
          animation: "toastTopIn 0.25s ease-out",
          maxWidth: "calc(100vw - 32px)", textAlign: "center",
        }}>
          {savedToast.text}
        </div>
      )}
      <style>{`@keyframes fadeIn{from{opacity:0;transform:scale(0.8)}to{opacity:1;transform:scale(1)}}@keyframes bqPulse{0%,100%{box-shadow:0 0 0 0 rgba(99,102,241,0.5)}50%{box-shadow:0 0 0 10px rgba(99,102,241,0)}}@keyframes badgePulse{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}@keyframes timerPulse{0%,100%{opacity:1}50%{opacity:0.6}}@keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}@keyframes toastTopIn{from{opacity:0;transform:translateX(-50%) translateY(-12px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
    </div>
  );
}

export { PlayScreen, mergeStates };
